import type { ReactNode } from 'react';
import {
  ChevronDown,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Dropdown, DropdownItem } from '../../navigation/Dropdown';

export interface DataGridToolbarProps {
  title: ReactNode;
  hasAnyGroup: boolean;
  allExpanded: boolean;
  allCollapsed: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** When null, no export dropdown is rendered at all. */
  exportConfig: {
    csv: boolean;
    excel: boolean;
  } | null;
  canExport: boolean;
  isExporting: boolean;
  onExportCsv: () => void;
  onExportExcel: () => void;
}

/**
 * Header bar rendered above the grid when at least one of `title`,
 * `exportable` or `grouping` is active. Hosts :
 *   - the title / subtitle slot on the left
 *   - the expand-all / collapse-all segmented buttons (when grouping)
 *   - the "Exporter" dropdown (when exportable)
 *
 * Kept in its own file so the main DataGrid component stays focused on
 * state and the body layout.
 */
export function DataGridToolbar({
  title,
  hasAnyGroup,
  allExpanded,
  allCollapsed,
  onExpandAll,
  onCollapseAll,
  exportConfig,
  canExport,
  isExporting,
  onExportCsv,
  onExportExcel,
}: DataGridToolbarProps): ReactNode {
  return (
    // Sous `sm`, les boutons passent sous le titre quand il ne reste pas au
    // moins 10rem au titre (`flex-wrap` + largeur mini du titre). A partir
    // de `sm`, classes d'origine uniquement.
    <div className="flex items-center justify-between gap-3 max-sm:flex-wrap">
      {title ? (
        <div className="min-w-0 flex-1 max-sm:min-w-[10rem]">
          {typeof title === 'string' ? (
            <h3 className="text-sm font-semibold text-dark-500">{title}</h3>
          ) : (
            title
          )}
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex items-center gap-2 flex-shrink-0 max-sm:flex-wrap">
        {hasAnyGroup && (
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={onExpandAll}
              disabled={allExpanded}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-dark-500 transition-colors',
                'hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed',
                'border-r border-neutral-200'
              )}
              title="Tout développer"
            >
              <ChevronsUpDown className="h-4 w-4 rotate-90" />
              <span className="hidden sm:inline">Tout développer</span>
            </button>
            <button
              type="button"
              onClick={onCollapseAll}
              disabled={allCollapsed}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-dark-500 transition-colors',
                'hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              title="Tout réduire"
            >
              <ChevronsUpDown className="h-4 w-4 -rotate-90" />
              <span className="hidden sm:inline">Tout réduire</span>
            </button>
          </div>
        )}

        {exportConfig && (exportConfig.csv || exportConfig.excel) && (
          <Dropdown
            align="right"
            trigger={
              <button
                type="button"
                disabled={!canExport || isExporting}
                className={cn(
                  'inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-dark-500 shadow-sm transition-colors',
                  'hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <Download className="h-4 w-4" />
                <span>{isExporting ? 'Export…' : 'Exporter'}</span>
                <ChevronDown className="h-4 w-4 text-neutral-400" />
              </button>
            }
          >
            {exportConfig.csv && (
              <DropdownItem icon={<FileText className="h-4 w-4" />} onClick={onExportCsv}>
                Exporter en CSV
              </DropdownItem>
            )}
            {exportConfig.excel && (
              <DropdownItem
                icon={<FileSpreadsheet className="h-4 w-4" />}
                onClick={onExportExcel}
              >
                Exporter en Excel
              </DropdownItem>
            )}
          </Dropdown>
        )}
      </div>
    </div>
  );
}
